import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Clock, Calendar, ArrowLeft, CheckCircle2, ArrowRight } from "lucide-react";
import { episodes as mockEpisodes } from "../mock/mock";
import { getEpisodes } from "../lib/api";

export default function EpisodeDetail() {
  const { id } = useParams();
  const [episodes, setEpisodes] = useState(mockEpisodes);

  useEffect(() => {
    getEpisodes()
      .then((data) => { if (Array.isArray(data) && data.length) setEpisodes(data); })
      .catch(() => {});
  }, []);

  const ep = episodes.find((e) => e.id === id) || episodes[0];
  const more = episodes.filter((e) => e.id !== ep.id).slice(0, 3);

  return (
    <div>
      <section className="bg-navy text-cream">
        <div className="max-w-4xl mx-auto px-5 py-14">
          <Link to="/podcast" className="inline-flex items-center gap-2 text-teal font-ui text-sm mb-8 hover:gap-3 transition-all">
            <ArrowLeft size={16} /> All Episodes
          </Link>
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="font-ui text-xs font-semibold text-teal">EP {ep.number}</span>
            <span className="font-ui text-xs px-2 py-0.5 rounded-full bg-white/10">{ep.pillar}</span>
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl font-bold">{ep.title}</h1>
          <div className="flex items-center gap-5 mt-5 text-white/60 font-ui text-sm">
            <span className="flex items-center gap-1.5"><Calendar size={15} /> {ep.date}</span>
            <span className="flex items-center gap-1.5"><Clock size={15} /> {ep.duration}</span>
          </div>
          <button className="mt-7 inline-flex items-center gap-2 bg-teal text-navy px-7 py-3.5 rounded-full font-ui font-semibold hover:bg-cream transition-colors">
            <Play size={18} fill="currentColor" /> Play Episode
          </button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 py-14 grid md:grid-cols-[1fr_300px] gap-12">
        <div>
          <div className="bg-cream-dark/60 rounded-xl p-5 mb-8 border-l-4" style={{ borderColor: "var(--teal)" }}>
            <p className="font-ui text-xs font-semibold text-teal uppercase tracking-wider mb-2">Episode Summary</p>
            <p className="font-ui text-navy/80 leading-relaxed">{ep.summary}</p>
          </div>

          <h2 className="font-serif-display text-2xl font-bold text-navy mb-4">In this episode</h2>
          <ul className="space-y-3 mb-10">
            {ep.showNotes.map((n) => (
              <li key={n} className="flex gap-3 font-ui text-navy/80">
                <CheckCircle2 size={20} className="text-teal shrink-0 mt-0.5" /> {n}
              </li>
            ))}
          </ul>
        </div>

        <aside>
          <div className="bg-white rounded-2xl p-6 border sticky top-24" style={{ borderColor: "var(--cream-dark)" }}>
            <p className="font-ui text-xs font-semibold text-teal uppercase tracking-wider mb-4">Guest</p>
            <img src={ep.guestImg} alt={ep.guest} className="w-full h-44 object-cover rounded-xl mb-4" />
            <h3 className="font-serif-display text-xl font-bold text-navy">{ep.guest}</h3>
            <p className="font-ui text-sm text-teal">{ep.guestRole}</p>
            <p className="font-ui text-sm text-navy/70 mt-3 leading-relaxed">{ep.guestBio}</p>
          </div>
        </aside>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="font-serif-display text-3xl font-bold text-navy mb-8">More episodes</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {more.map((e) => (
              <Link key={e.id} to={`/podcast/${e.id}`} className="lift bg-cream rounded-2xl overflow-hidden border" style={{ borderColor: "var(--cream-dark)" }}>
                <img src={e.thumb} alt={e.title} className="w-full h-36 object-cover" />
                <div className="p-5">
                  <span className="font-ui text-xs font-semibold text-teal">EP {e.number}</span>
                  <h3 className="font-serif-display text-lg font-semibold text-navy mt-1 leading-tight">{e.title}</h3>
                  <span className="inline-flex items-center gap-1 text-teal font-ui text-sm mt-3">Listen <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
